import { useEffect, useRef, useState } from 'react';
import {
  getAuth,
  GoogleAuthProvider,
  getMultiFactorResolver,
  signInWithCredential,
} from 'firebase/auth';

import {
  SIGN_IN_DESTINATIONS,
  decideSignInDestination,
} from '../policy/signInPolicy';
import { resolveGoogleTenant } from '../policy/googleTenant';
import { loadGoogleIdentity, emailFromIdToken } from '../policy/googleIdentity';
import { getCurrentUserTokens } from '../policy/sessionTokens';
import { getSafeErrorIdentifier } from '../../utils/getSafeErrorIdentifier';

const isBrowserBridge = () =>
  typeof window !== 'undefined' && !!window.ReactNativeWebView;

// Renders Google's own account chooser into `buttonRef` and resolves the real
// tenant before Firebase is told anything: there is no wrong-tenant sign-in
// to correct afterwards, because Firebase only sees the credential once the
// tenant is already decided.
export default function useGoogleSignIn({
  buttonRef,
  tenantID,
  language,
  isBusy,
  resolveTenant,
  getSessionCookie,
  setResolverAuth,
  showMfaEnrollmentChooser,
  setLoginErrorKey,
  setIsLoading,
  setActiveMethod,
}) {
  const handleCredentialRef = useRef();
  // Mirrors `isBusy` for the handler to read synchronously — a closure over
  // the prop would only ever see the render that created it.
  const isBusyRef = useRef(isBusy);
  // Own re-entrancy guard: GIS can hand a second credential while the first
  // is still resolving, before isBusy's re-render commits.
  const processingRef = useRef(false);

  // Bruno's WebView injects this global; GIS itself is a dead end there
  // (Google blocks it in WebViews), so the bridge stands in for it instead.
  //
  // Read as state, not a render-time constant: the native side wires this up
  // through its own async prop application, so it can still be absent on the
  // very first render even though it always arrives — see the handoff doc.
  const [isBridge, setIsBridge] = useState(isBrowserBridge);

  // Corrects a false reading above: retries in case the bridge object
  // attaches a beat after this component's first render. Starts fast (the
  // common case on a warm WebView) and backs off, because a cold WebView
  // start on real hardware can take much longer than dev tooling or a
  // simulator ever shows — a short fixed budget missed that gap entirely.
  useEffect(() => {
    if (isBridge) return undefined;
    let cancelled = false;
    let delay = 20;
    let elapsed = 0;
    const budget = 5000;
    const recheck = () => {
      if (cancelled) return;
      if (isBrowserBridge()) {
        setIsBridge(true);
        return;
      }
      if (elapsed >= budget) return;
      setTimeout(() => {
        elapsed += delay;
        delay = Math.min(delay * 2, 1000);
        recheck();
      }, delay);
    };
    recheck();
    return () => {
      cancelled = true;
    };
  }, [isBridge]);

  // Not a dependency: every field this closure reads can change across
  // renders, and a bare effect (no deps) keeps `.current` on the latest one
  // without writing a ref during render.
  useEffect(() => {
    isBusyRef.current = isBusy;
    handleCredentialRef.current = async idToken => {
      if (processingRef.current || isBusyRef.current) return;
      processingRef.current = true;
      setActiveMethod('google');
      setIsLoading(true);
      setLoginErrorKey(null);

      let email;
      try {
        email = emailFromIdToken(idToken);
      } catch (error) {
        console.error('useGoogleSignIn:', getSafeErrorIdentifier(error));
        setLoginErrorKey('heimdal_app.Login.try_again_later');
        setIsLoading(false);
        processingRef.current = false;
        return;
      }

      const gate = await resolveGoogleTenant({ email, tenantID, resolveTenant });
      if (gate.blocked) {
        setLoginErrorKey('heimdal_app.Login.login_error');
        setIsLoading(false);
        processingRef.current = false;
        return;
      }

      const auth = getAuth();
      auth.tenantId = gate.tenantId;
      auth.languageCode = language;

      try {
        const result = await signInWithCredential(
          auth,
          GoogleAuthProvider.credential(idToken),
        );
        const user = result.user;
        const destination = decideSignInDestination(user);
        if (destination === SIGN_IN_DESTINATIONS.MFA_ENROLLMENT_CHOOSER) {
          setIsLoading(true);
          showMfaEnrollmentChooser(user);
        } else {
          getSessionCookie(await getCurrentUserTokens());
        }
      } catch (error) {
        if (error.code === 'auth/multi-factor-auth-required') {
          setIsLoading(true);
          setResolverAuth(getMultiFactorResolver(auth, error), gate.tenantId);
          // isBusy (from isLoading, set above) keeps the button disabled
          // during the handoff — this ref only guards a stray credential
          // arriving mid-flight, so releasing it here can't re-enable a
          // click early, only prevent one from being stranded forever.
          processingRef.current = false;
        } else if (error.code === 'auth/user-disabled') {
          setLoginErrorKey('heimdal_app.Login.user_disabled');
          setIsLoading(false);
          processingRef.current = false;
        } else {
          console.error('useGoogleSignIn:', getSafeErrorIdentifier(error));
          setLoginErrorKey('heimdal_app.Login.try_again_later');
          setIsLoading(false);
          processingRef.current = false;
        }
      }
    };
  });

  // isBridge is a real dependency: once the retry above corrects a false
  // reading, this effect must cancel any GIS load already in flight.
  useEffect(() => {
    if (isBridge || !buttonRef.current) return undefined;
    let cancelled = false;
    loadGoogleIdentity()
      .then(google => {
        if (cancelled || !buttonRef.current) return;
        google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          callback: response => {
            if (response?.credential) {
              handleCredentialRef.current(response.credential);
            }
          },
        });
        google.accounts.id.renderButton(buttonRef.current, {
          theme: 'outline',
          size: 'large',
        });
      })
      .catch(error => {
        // Never leave this unhandled: a network hiccup, or the retry above
        // losing its race, must not crash the whole screen over a button
        // that simply won't render.
        if (!cancelled) {
          console.error('useGoogleSignIn:', getSafeErrorIdentifier(error));
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBridge]);

  // The bridge's one-shot answer: Bruno's WebView calls it directly
  // (`injectJavaScript`) once per native sign-in attempt, then it is gone.
  useEffect(() => {
    if (!isBridge) return undefined;
    window.__heimdalGoogleSignInResult = idToken => {
      delete window.__heimdalGoogleSignInResult;
      if (typeof idToken === 'string' && idToken) {
        handleCredentialRef.current(idToken);
      } else {
        setLoginErrorKey('heimdal_app.Login.try_again_later');
      }
    };
    return () => {
      delete window.__heimdalGoogleSignInResult;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBridge]);

  // Asks the RN side to start native sign-in. Re-entrancy uses the same
  // guards the credential handler itself checks first.
  const startBridgeSignIn = () => {
    if (processingRef.current || isBusyRef.current) return;
    window.ReactNativeWebView.postMessage(
      JSON.stringify({ type: 'GOOGLE_SIGN_IN' }),
    );
  };

  return { isBridge, startBridgeSignIn };
}
